/**
 * Auth context and hook, kept apart from the provider component.
 *
 * Vite's Fast Refresh only preserves state for a module that exports nothing
 * but components. A context file that also exports its hook is reloaded as a
 * whole, so every edit to AuthProvider used to log the developer out. The
 * provider lives in AuthContext.tsx and imports from here.
 */
import { createContext, useContext } from 'react'

export interface User {
  id: string
  email: string
  is_active: boolean
  is_admin: boolean
  must_change_password: boolean
  created_at: string
}

export interface AuthContextType {
  user: User | null
  accessToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, inviteToken: string) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  reloadUser: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * useAuth - Hook to access authentication context
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
